import { useState } from "react";
import { BtnPrimary, Card, Input, Pill, Select } from "../components/ui";
import { PEPTIDE_LIBRARY } from "../data/peptides";
import { S, css } from "../theme";

function generateCalendarFile(protocol, reminderTime) {
  const [hh, mm] = (reminderTime || "08:00").split(":");
  const pad = (n) => String(n).padStart(2, "0");
  const now = new Date();
  const dtstamp = now.getFullYear() + pad(now.getMonth() + 1) + pad(now.getDate()) + "T" + pad(now.getHours()) + pad(now.getMinutes()) + "00";

  const start = new Date();
  start.setDate(start.getDate() + 1);
  const dateStr = start.getFullYear() + pad(start.getMonth() + 1) + pad(start.getDate());

  const NL = String.fromCharCode(13) + String.fromCharCode(10);
  let ics = "BEGIN:VCALENDAR" + NL + "VERSION:2.0" + NL + "PRODID:-//TrackMyPeptide//EN" + NL + "CALSCALE:GREGORIAN" + NL;

  protocol.forEach((p, i) => {
    let rrule = "FREQ=DAILY";
    const freq = (p.frequency || "").toLowerCase();
    // Match "every N days" pattern (e.g. "every 5 days", "Every 6 days")
    const everyMatch = freq.match(/every\s+(\d+)\s+day/);
    if (everyMatch) {
      const n = parseInt(everyMatch[1], 10);
      if (n >= 1) rrule = "FREQ=DAILY;INTERVAL=" + n;
    } else if (freq.includes("week")) {
      if (freq.includes("2") || freq.includes("twice")) rrule = "FREQ=WEEKLY;BYDAY=MO,TH";
      else if (freq.includes("3")) rrule = "FREQ=WEEKLY;BYDAY=MO,WE,FR";
      else rrule = "FREQ=WEEKLY";
    } else if (freq.includes("every other") || freq.includes("eod")) {
      rrule = "FREQ=DAILY;INTERVAL=2";
    }
    const uid = "tmp-" + Date.now() + "-" + i + "@trackmypeptide.com";
    ics += "BEGIN:VEVENT" + NL;
    ics += "UID:" + uid + NL;
    ics += "DTSTAMP:" + dtstamp + NL;
    ics += "DTSTART:" + dateStr + "T" + pad(hh) + pad(mm) + "00" + NL;
    ics += "DURATION:PT15M" + NL;
    ics += "RRULE:" + rrule + NL;
    ics += "SUMMARY:Peptide dose: " + p.name + " (" + (p.dose || "") + ")" + NL;
    ics += "DESCRIPTION:Time for your " + p.name + " dose. Log it in TrackMyPeptide: https://trackmypeptide.com" + NL;
    ics += "BEGIN:VALARM" + NL + "TRIGGER:PT0M" + NL + "ACTION:DISPLAY" + NL + "DESCRIPTION:Time for your " + p.name + " dose" + NL + "END:VALARM" + NL;
    ics += "END:VEVENT" + NL;
  });

  ics += "END:VCALENDAR";
  return ics;
}

function downloadCalendar(protocol, reminderTime) {
  const ics = generateCalendarFile(protocol, reminderTime);
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "trackmypeptide-reminders.ics";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function ProtocolBuilder({ protocol, setProtocol }) {
  const [showCal, setShowCal] = useState(false);
  const [reminderTime, setReminderTime] = useState("08:00");
  const [showAdd, setShowAdd] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [dose, setDose] = useState("");
  const [route, setRoute] = useState("SubQ");
  const [frequency, setFrequency] = useState("Daily");
  const [customDays, setCustomDays] = useState("5"); // for "Every X days"
  const [notes, setNotes] = useState("");

  // ── Custom peptide ("Add your own") state ──────────────────
  const [mode, setMode] = useState("library"); // "library" | "custom"
  const [customName, setCustomName] = useState("");
  const [customCategory, setCustomCategory] = useState("Custom");

  // ── Edit flow state ─────────────────────────────────────────
  // When editingId is set, the Add Peptide form becomes an Edit form
  // and "Save" updates the existing protocol row in place (preserving id, addedAt)
  const [editingId, setEditingId] = useState(null);
  // Edit-only "Started on" date — pre-filled from the peptide, only used during edit
  const [startDateEdit, setStartDateEdit] = useState("");

  const filtered = PEPTIDE_LIBRARY.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.category.toLowerCase().includes(search.toLowerCase())
  ).filter(p => !protocol.find(pr => pr.id === p.id));

  // Resolve the final frequency string — handles "Every X days"
  function resolveFrequency() {
    if (frequency === "Every X days") {
      const n = parseInt(customDays, 10);
      if (!n || n < 1) return "Every 5 days"; // safe fallback
      return n === 1 ? "Daily" : `Every ${n} days`;
    }
    return frequency;
  }

  function addPeptide() {
    if (!selected || !dose) return;
    const lib = PEPTIDE_LIBRARY.find(p => p.id === selected);
    setProtocol(prev => [...prev, { ...lib, dose, route, frequency: resolveFrequency(), notes, addedAt: new Date().toISOString(), startDate: new Date().toDateString() }]);
    resetAddForm();
  }

  function addCustomPeptide() {
    const trimmedName = customName.trim();
    if (!trimmedName || !dose) return;

    // Unique id so tracking/calendar work; "custom_" prefix lets us flag these later
    const id = "custom_" + Math.random().toString(36).slice(2, 10) + "_" + Date.now().toString(36);

    // Use the ember accent color as the default for custom peptides
    const color = S.accent2;

    setProtocol(prev => [
      ...prev,
      {
        id,
        name: trimmedName,
        category: customCategory || "Custom",
        color,
        summary: `Custom peptide added by you. ${dose} · ${route} · ${resolveFrequency()}${notes ? ` · ${notes}` : ""}`,
        dosing: dose,
        route,
        cycle: "",
        tags: ["Custom"],
        isCustom: true,
        dose,
        frequency: resolveFrequency(),
        notes,
        addedAt: new Date().toISOString(),
        startDate: new Date().toDateString(),
      },
    ]);
    resetAddForm();
  }

  // ── Edit existing peptide ───────────────────────────────────
  // Pre-fills the form with the peptide's current values so the user can change them.
  function startEdit(p) {
    // Mutual exclusivity: opening edit closes the calendar reminders modal
    setShowCal(false);

    setEditingId(p.id);
    setShowAdd(true);
    // Pre-fill all the form fields with current values
    setDose(p.dose || "");
    setRoute(p.route || "SubQ");
    setNotes(p.notes || "");
    setStartDateEdit(toDateInputValue(p.startDate));

    // Frequency: reverse-parse "Every N days" back into dropdown + customDays
    const freq = p.frequency || "Daily";
    const everyMatch = freq.match(/^Every\s+(\d+)\s+days?$/i);
    if (everyMatch) {
      setFrequency("Every X days");
      setCustomDays(everyMatch[1]);
    } else {
      setFrequency(freq);
      setCustomDays("5");
    }

    // Mode: custom or library? Custom peptides edit in "custom" mode (lets user change name)
    if (p.isCustom) {
      setMode("custom");
      setCustomName(p.name || "");
      setCustomCategory(p.category || "Custom");
    } else {
      setMode("library");
      setSelected(p.id);
      setSearch(p.name || "");
    }
  }

  // Saves edits back to the existing protocol row, preserving id, startDate, addedAt, and tracking history
  function saveEdit() {
    if (!editingId) return;
    // For custom: require name; for library: require dose
    if (mode === "custom" && !customName.trim()) return;
    if (!dose) return;

    // Compute new startDate (only if user picked a valid one)
    let newStartDate = null;
    if (startDateEdit) {
      const d = new Date(startDateEdit + "T00:00:00");
      if (!isNaN(d.getTime())) newStartDate = d.toDateString();
    }

    setProtocol(prev => prev.map(p => {
      if (p.id !== editingId) return p;
      const base = {
        ...p,
        dose,
        route,
        frequency: resolveFrequency(),
        notes,
        ...(newStartDate ? { startDate: newStartDate } : {}),
      };
      // Library peptide edit: name/category locked to library entry
      if (!p.isCustom) return base;
      // Custom peptide edit — also allow name + category change
      const trimmedName = customName.trim();
      return {
        ...base,
        name: trimmedName,
        category: customCategory || "Custom",
        dosing: dose,
        summary: `Custom peptide added by you. ${dose} · ${route} · ${resolveFrequency()}${notes ? ` · ${notes}` : ""}`,
      };
    }));
    resetAddForm();
  }

  // Convert a toDateString() (e.g. "Mon May 19 2026") back to "YYYY-MM-DD" for the date input
  function toDateInputValue(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  function resetAddForm() {
    setShowAdd(false);
    setMode("library");
    setSelected(null);
    setSearch("");
    setDose("");
    setRoute("SubQ");
    setFrequency("Daily");
    setCustomDays("5");
    setNotes("");
    setCustomName("");
    setCustomCategory("Custom");
    setEditingId(null);
    setStartDateEdit("");
  }

  function removePeptide(id) {
    setProtocol(prev => prev.filter(p => p.id !== id));
  }

  return (
    <div style={{ padding: 40 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 32 }}>
        <div>
          <h2 style={{ ...css.fontDisplay, fontSize: "1.8rem", fontWeight: 900, color: S.ink, letterSpacing: "-0.03em", margin: 0 }}>My Protocol</h2>
          <p style={{ color: S.muted, fontSize: "0.85rem", marginTop: 6 }}>{protocol.length} peptide{protocol.length !== 1 ? "s" : ""} in your current stack</p>
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {protocol.length > 0 && (
            <button
              onClick={() => {
                // Mutual exclusivity — if opening reminders, close add/edit form
                if (!showCal && showAdd) resetAddForm();
                setShowCal(!showCal);
              }}
              style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: "0.9rem", color: S.accent, background: S.accentLight, border: `1.5px solid ${S.accent}44`, padding: "12px 22px", borderRadius: 100, cursor: "pointer" }}
            >
              📅 Reminders
            </button>
          )}
          <BtnPrimary onClick={() => {
            if (showAdd) {
              resetAddForm();
            } else {
              // Opening add form — close reminders if open
              if (showCal) setShowCal(false);
              setShowAdd(true);
            }
          }}>{showAdd ? "✕ Cancel" : "+ Add Peptide"}</BtnPrimary>
        </div>
      </div>

      {showCal && protocol.length > 0 && (
        <Card style={{ marginBottom: 28, background: "linear-gradient(135deg, #19211d, #16271f)", borderColor: `${S.accent}33` }}>
          <h3 style={{ ...css.fontDisplay, fontWeight: 700, fontSize: "1.1rem", color: S.ink, marginBottom: 6 }}>📅 Add dose reminders to your calendar</h3>
          <p style={{ fontSize: "0.85rem", color: S.muted, lineHeight: 1.7, marginBottom: 20 }}>
            Get reminded right on your phone — we'll create recurring reminders for each peptide in your calendar app (Apple Calendar, Google Calendar, etc). Your phone handles the notifications automatically.
          </p>
          <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div>
              <label style={{ display: "block", fontSize: "0.78rem", fontWeight: 600, color: S.ink2, marginBottom: 6 }}>Reminder time each day</label>
              <input type="time" value={reminderTime} onChange={e => setReminderTime(e.target.value)} style={{ padding: "11px 14px", border: `1.5px solid ${S.border}`, borderRadius: 12, fontSize: "0.9rem", fontFamily: "'Inter', sans-serif", background: S.white, color: S.ink, outline: "none" }} />
            </div>
            <BtnPrimary onClick={() => downloadCalendar(protocol, reminderTime)}>Download reminders →</BtnPrimary>
          </div>
          <p style={{ fontSize: "0.72rem", color: S.muted, marginTop: 16, lineHeight: 1.6, marginBottom: 0 }}>
            Tip: After downloading, tap the file and your phone will offer to add the reminders to your calendar. Reminders repeat based on each peptide's frequency. If you change your protocol later, just download again.
          </p>
        </Card>
      )}

      {showAdd && (
        <Card style={{ marginBottom: 28, background: S.accentLight, borderColor: `${S.accent}44` }}>
          <h3 style={{ ...css.fontDisplay, fontWeight: 700, fontSize: "1.1rem", color: S.ink, marginBottom: 14 }}>
            {editingId ? "Edit Peptide" : "Add a Peptide"}
          </h3>

          {/* Mode toggle: from library vs add your own — only shown when adding, not when editing */}
          {!editingId && (
            <div style={{ display: "flex", gap: 8, marginBottom: 18, background: S.surface2, padding: 4, borderRadius: 100, width: "fit-content" }}>
              <button
                onClick={() => setMode("library")}
                style={{
                  padding: "8px 16px", borderRadius: 100, border: "none",
                  background: mode === "library" ? S.accent : "transparent",
                  color: mode === "library" ? "#fff" : S.muted,
                  fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: "0.78rem",
                  cursor: "pointer", transition: "all 0.15s",
                }}
              >
                From library
              </button>
              <button
                onClick={() => { setMode("custom"); setSelected(null); setSearch(""); }}
                style={{
                  padding: "8px 16px", borderRadius: 100, border: "none",
                  background: mode === "custom" ? S.accent : "transparent",
                  color: mode === "custom" ? "#fff" : S.muted,
                  fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: "0.78rem",
                  cursor: "pointer", transition: "all 0.15s",
                }}
              >
                + Add your own
              </button>
            </div>
          )}

          {/* ── FROM LIBRARY MODE ─────────────────────────── */}
          {mode === "library" && (
            <>
              {/* When editing, hide search + list — the user already has this peptide and just wants to update its details */}
              {!editingId && (
                <>
                  <Input label="Search peptides" value={search} onChange={e => { setSearch(e.target.value); setSelected(null); }} placeholder="e.g. BPC-157, Recovery..." />

                  {search && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16, maxHeight: 200, overflowY: "auto" }}>
                      {filtered.length === 0 && (
                        <div style={{ padding: 14, background: S.white, borderRadius: 12, border: `1px dashed ${S.border}` }}>
                          <p style={{ color: S.muted, fontSize: "0.82rem", margin: 0, marginBottom: 8 }}>No matches in our library yet.</p>
                          <button
                            onClick={() => { setMode("custom"); setCustomName(search); }}
                            style={{
                              background: "transparent", border: "none", padding: 0,
                              color: S.accent, fontFamily: "'Inter', sans-serif", fontWeight: 600,
                              fontSize: "0.82rem", cursor: "pointer", textDecoration: "underline",
                            }}
                          >
                            Add "{search}" yourself →
                          </button>
                        </div>
                      )}
                      {filtered.map(p => (
                        <div key={p.id} onClick={() => { setSelected(p.id); setDose(p.dosing.split("–")[0].trim()); }} style={{ padding: "12px 16px", borderRadius: 12, border: `1.5px solid ${selected === p.id ? S.accent : S.border}`, background: selected === p.id ? S.white : S.white, cursor: "pointer", transition: "all 0.15s" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <div style={{ width: 8, height: 8, borderRadius: "50%", background: p.color }} />
                            <span style={{ fontWeight: 700, fontSize: "0.9rem", color: S.accent2 }}>{p.name}</span>
                            <Pill color={S.muted} bg={S.surface2}>{p.category}</Pill>
                          </div>
                          <p style={{ fontSize: "0.75rem", color: S.muted, marginTop: 4, marginBottom: 0, marginLeft: 18 }}>{p.summary.slice(0, 80)}...</p>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              {/* Form fields — shown when adding (after a library peptide is selected) OR when editing */}
              {(selected || editingId) && (
                <>
                  {editingId && (
                    <div style={{ marginBottom: 14, padding: "10px 14px", background: S.white, borderRadius: 10, border: `1px solid ${S.border}` }}>
                      <span style={{ fontSize: "0.78rem", color: S.muted, fontFamily: "'Fraunces', serif", fontStyle: "italic" }}>Editing: </span>
                      <span style={{ fontSize: "0.88rem", fontWeight: 600, color: S.accent2 }}>
                        {protocol.find(p => p.id === editingId)?.name}
                      </span>
                    </div>
                  )}
                  <Input label="Dose (e.g. 250mcg)" value={dose} onChange={e => setDose(e.target.value)} placeholder="250mcg" />
                  <Select label="Route" value={route} onChange={e => setRoute(e.target.value)} options={["SubQ", "IM", "Intranasal", "Oral", "Topical", "IV"].map(v => ({ value: v, label: v }))} />
                  <Select label="Frequency" value={frequency} onChange={e => setFrequency(e.target.value)} options={["Daily", "2x/day", "EOD", "2x/week", "Weekly", "Every X days", "As needed"].map(v => ({ value: v, label: v }))} />
                  {frequency === "Every X days" && (
                    <div style={{ marginTop: -8, marginBottom: 16, padding: "12px 14px", background: S.white, borderRadius: 12, border: `1.5px solid ${S.accent}33` }}>
                      <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 600, color: S.ink2, marginBottom: 6 }}>How many days between doses?</label>
                      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        <span style={{ fontSize: "0.85rem", color: S.muted, fontFamily: "'Fraunces', serif", fontStyle: "italic" }}>every</span>
                        <input
                          type="number"
                          min="1"
                          max="60"
                          value={customDays}
                          onChange={e => setCustomDays(e.target.value)}
                          style={{ width: 70, padding: "8px 12px", border: `1.5px solid ${S.border}`, borderRadius: 10, fontSize: "0.9rem", fontFamily: "'Inter', sans-serif", background: S.white, color: S.ink, outline: "none", textAlign: "center" }}
                        />
                        <span style={{ fontSize: "0.85rem", color: S.muted, fontFamily: "'Fraunces', serif", fontStyle: "italic" }}>days</span>
                      </div>
                    </div>
                  )}
                  <Input label="Notes (optional)" value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. Take before bed" />
                  {editingId && (
                    <div style={{ marginBottom: 16 }}>
                      <label style={{ display: "block", fontSize: "0.78rem", fontWeight: 600, color: S.ink2, marginBottom: 6 }}>Started on</label>
                      <input
                        type="date"
                        value={startDateEdit}
                        onChange={e => setStartDateEdit(e.target.value)}
                        style={{ width: "100%", padding: "11px 14px", border: `1.5px solid ${S.border}`, borderRadius: 12, fontSize: "0.9rem", fontFamily: "'Inter', sans-serif", background: S.white, color: S.ink, outline: "none" }}
                      />
                      <p style={{ fontSize: "0.72rem", color: S.muted, marginTop: 6, marginBottom: 0, fontStyle: "italic" }}>
                        Changing this only updates the label. Your dose log history stays put.
                      </p>
                    </div>
                  )}
                  <BtnPrimary onClick={editingId ? saveEdit : addPeptide}>
                    {editingId ? "Save changes →" : "Add to Protocol →"}
                  </BtnPrimary>
                </>
              )}
            </>
          )}

          {/* ── CUSTOM PEPTIDE MODE ───────────────────────── */}
          {mode === "custom" && (
            <>
              {!editingId && (
                <p style={{ fontSize: "0.82rem", color: S.muted, marginBottom: 18, lineHeight: 1.6, fontFamily: "'Fraunces', serif", fontStyle: "italic" }}>
                  Don't see your peptide? Add it here. Lives only on your protocol — others won't see it.
                </p>
              )}
              <Input label="Peptide name" value={customName} onChange={e => setCustomName(e.target.value)} placeholder="e.g. GHK-Cu, Glow, Reta..." />
              <Input label="Dose (e.g. 250mcg, 0.5mg, 2mg)" value={dose} onChange={e => setDose(e.target.value)} placeholder="250mcg" />
              <Select label="Route" value={route} onChange={e => setRoute(e.target.value)} options={["SubQ", "IM", "Intranasal", "Oral", "Topical", "IV"].map(v => ({ value: v, label: v }))} />
              <Select label="Frequency" value={frequency} onChange={e => setFrequency(e.target.value)} options={["Daily", "2x/day", "EOD", "2x/week", "Weekly", "Every X days", "As needed"].map(v => ({ value: v, label: v }))} />
              {frequency === "Every X days" && (
                <div style={{ marginTop: -8, marginBottom: 16, padding: "12px 14px", background: S.white, borderRadius: 12, border: `1.5px solid ${S.accent}33` }}>
                  <label style={{ display: "block", fontSize: "0.75rem", fontWeight: 600, color: S.ink2, marginBottom: 6 }}>How many days between doses?</label>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: "0.85rem", color: S.muted, fontFamily: "'Fraunces', serif", fontStyle: "italic" }}>every</span>
                    <input
                      type="number"
                      min="1"
                      max="60"
                      value={customDays}
                      onChange={e => setCustomDays(e.target.value)}
                      style={{ width: 70, padding: "8px 12px", border: `1.5px solid ${S.border}`, borderRadius: 10, fontSize: "0.9rem", fontFamily: "'Inter', sans-serif", background: S.white, color: S.ink, outline: "none", textAlign: "center" }}
                    />
                    <span style={{ fontSize: "0.85rem", color: S.muted, fontFamily: "'Fraunces', serif", fontStyle: "italic" }}>days</span>
                  </div>
                </div>
              )}
              <Input label="Notes (optional)" value={notes} onChange={e => setNotes(e.target.value)} placeholder="e.g. cycles with 2 days off" />
              {editingId && (
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: "block", fontSize: "0.78rem", fontWeight: 600, color: S.ink2, marginBottom: 6 }}>Started on</label>
                  <input
                    type="date"
                    value={startDateEdit}
                    onChange={e => setStartDateEdit(e.target.value)}
                    style={{ width: "100%", padding: "11px 14px", border: `1.5px solid ${S.border}`, borderRadius: 12, fontSize: "0.9rem", fontFamily: "'Inter', sans-serif", background: S.white, color: S.ink, outline: "none" }}
                  />
                  <p style={{ fontSize: "0.72rem", color: S.muted, marginTop: 6, marginBottom: 0, fontStyle: "italic" }}>
                    Changing this only updates the label. Your dose log history stays put.
                  </p>
                </div>
              )}
              <BtnPrimary onClick={editingId ? saveEdit : addCustomPeptide}>
                {editingId ? "Save changes →" : "Add to Protocol →"}
              </BtnPrimary>
              {!editingId && (!customName.trim() || !dose) && (
                <p style={{ fontSize: "0.72rem", color: S.muted, marginTop: 10, fontStyle: "italic" }}>
                  Enter at least a name and dose to save.
                </p>
              )}
            </>
          )}
        </Card>
      )}

      {protocol.length === 0 ? (
        <Card style={{ textAlign: "center", padding: 60 }}>
          <div style={{ fontSize: "3rem", marginBottom: 16 }}>💉</div>
          <div style={{ ...css.fontDisplay, fontSize: "1.3rem", fontWeight: 700, marginBottom: 8 }}>No peptides yet</div>
          <p style={{ color: S.muted, fontSize: "0.88rem" }}>Click "Add Peptide" to build your protocol.</p>
        </Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {protocol.map(p => (
            <Card key={p.id} style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <div style={{ width: 48, height: 48, borderRadius: 14, background: `${p.color}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <div style={{ width: 14, height: 14, borderRadius: "50%", background: p.color }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                  <span style={{ fontWeight: 700, fontSize: "1rem", color: S.accent2 }}>{p.name}</span>
                  <Pill color={S.muted} bg={S.surface2}>{p.category}</Pill>
                </div>
                <div style={{ fontSize: "0.8rem", color: S.muted }}>
                  {p.dose} · {p.route} · {p.frequency}
                  {p.notes && <span> · {p.notes}</span>}
                </div>
              </div>
              {/* Start date — read-only label. To change it, open the edit form. */}
              <div style={{ fontSize: "0.72rem", color: S.muted, textAlign: "right", marginRight: 16 }}>
                <div>Started</div>
                <div style={{ fontWeight: 600, color: S.ink2 }}>{p.startDate}</div>
              </div>
              {/* Edit pencil button */}
              <button
                onClick={() => startEdit(p)}
                title="Edit peptide"
                style={{
                  width: 36, height: 36, borderRadius: 10,
                  border: `1px solid ${S.border}`, background: "transparent",
                  cursor: "pointer", fontSize: "0.9rem", color: S.ink2,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  marginRight: 6,
                }}
              >
                ✎
              </button>
              <button onClick={() => removePeptide(p.id)} title="Remove peptide" style={{ width: 36, height: 36, borderRadius: 10, border: `1px solid ${S.border}`, background: "transparent", cursor: "pointer", fontSize: "1rem", color: "#dc2626", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
            </Card>
          ))}
        </div>
      )}

      <div style={{ marginTop: 24, padding: "16px 20px", background: S.accent2Light, borderRadius: 14, border: `1px solid ${S.accent2}33` }}>
        <p style={{ fontSize: "0.75rem", color: "#7c4a2a", margin: 0, lineHeight: 1.6 }}>
          ⚠️ <strong>Educational use only.</strong> This tracker is for logging purposes. The dose suggestions shown are from published research — they are not medical recommendations. Always work with a licensed physician before starting any peptide protocol.
        </p>
      </div>
    </div>
  );
}

// ─── INJECTION SITES (shared by Track check-off + Injection overview) ──
// Body zones — front and back, with rough x/y % positions on the diagram.

export { ProtocolBuilder };
